/**
 * Escáner de código de barras (2026-10-21, Elvio: "prendido con lo que hay"): la cámara de la tablet lee
 * el código de la caja que trae el cliente y se consulta como si se hubiera escrito. Todo en la tablet:
 * la imagen no sale del equipo (BarcodeDetector de Chrome en Android). Los códigos vienen de los vínculos
 * con Chávez y Farmacorp (~1.100 productos) hasta que la API de Hipermaxi traiga el suyo; si no lo tiene,
 * el asistente lo dice y pide la marca.
 *
 * Sin cámara o sin BarcodeDetector (navegador de escritorio, iPhone), el botón no aparece.
 */
import { useEffect, useRef, useState } from 'react'
import { Icon } from './icons'

interface Detectado {
  rawValue: string
}
interface Detector {
  detect: (fuente: HTMLVideoElement) => Promise<Detectado[]>
}
type ConstructorDetector = new (opciones: { formats: string[] }) => Detector

const FORMATOS = ['ean_13', 'ean_8', 'upc_a', 'upc_e']

function detector(): ConstructorDetector | null {
  const w = window as unknown as { BarcodeDetector?: ConstructorDetector }
  return w.BarcodeDetector && typeof navigator.mediaDevices?.getUserMedia === 'function' ? w.BarcodeDetector : null
}

interface Props {
  onCodigo: (codigo: string) => void
  disabled?: boolean
}

export function Escaner({ onCodigo, disabled = false }: Props) {
  const [abierto, setAbierto] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const video = useRef<HTMLVideoElement | null>(null)
  const flujo = useRef<MediaStream | null>(null)
  const cancelarRef = useRef<HTMLButtonElement | null>(null)

  const cerrar = () => {
    flujo.current?.getTracks().forEach((t) => t.stop())
    flujo.current = null
    setAbierto(false)
    setError(null)
  }

  useEffect(() => {
    if (!abierto) return
    const Clase = detector()
    if (!Clase) return
    let vivo = true
    let reloj: ReturnType<typeof setTimeout> | undefined
    const det = new Clase({ formats: FORMATOS })
    cancelarRef.current?.focus()
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: 'environment' }, audio: false })
      .then(async (s) => {
        if (!vivo) {
          s.getTracks().forEach((t) => t.stop())
          return
        }
        flujo.current = s
        if (video.current) {
          video.current.srcObject = s
          try {
            await Promise.resolve(video.current.play()).catch(() => {})
          } catch {
            /* sin autoplay: el detector igual mira el cuadro */
          }
        }
        const mirar = async () => {
          if (!vivo || !video.current) return
          try {
            const hallados = await det.detect(video.current)
            const codigo = hallados.find((h) => /^\d{8,14}$/.test(h.rawValue))?.rawValue
            if (codigo) {
              cerrar()
              onCodigo(codigo)
              return
            }
          } catch {
            /* cuadro sin imagen todavía: se reintenta */
          }
          reloj = setTimeout(mirar, 250)
        }
        mirar()
      })
      .catch(() => setError('No se pudo abrir la cámara. Revisá el permiso de cámara del navegador.'))
    return () => {
      vivo = false
      if (reloj) clearTimeout(reloj)
      flujo.current?.getTracks().forEach((t) => t.stop())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierto])

  if (!detector()) return null

  return (
    <>
      <button
        type="button"
        aria-label="Escanear código de barras"
        title="Escanear el código de barras de la caja"
        onClick={() => setAbierto(true)}
        disabled={disabled}
        className="grid place-items-center"
        style={{
          minHeight: 44, minWidth: 44, width: 44, height: 44, borderRadius: 999,
          border: '1px solid var(--color-border-strong)', background: 'var(--color-surface)',
          color: 'var(--color-text-muted)', cursor: disabled ? 'not-allowed' : 'pointer',
        }}
      >
        <Icon.Barras />
      </button>
      {abierto && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Escanear código de barras"
          onKeyDown={(e) => e.key === 'Escape' && cerrar()}
          className="escaner-fondo"
        >
          <div className="escaner-caja">
            <div className="escaner-titulo">Apuntá al código de barras de la caja</div>
            {error ? (
              <div role="alert" className="escaner-error">{error}</div>
            ) : (
              <div className="escaner-visor">
                <video ref={video} playsInline muted aria-label="Vista de la cámara" />
                <div className="escaner-guia" aria-hidden="true" />
              </div>
            )}
            <button ref={cancelarRef} type="button" className="escaner-cancelar" onClick={cerrar}>
              Cancelar
            </button>
          </div>
        </div>
      )}
    </>
  )
}
